import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

const showToast = (message, isError = false) => {
  const toast = document.createElement("div");
  toast.textContent = message;
  toast.className = isError
    ? "verify-email-page__toast--error"
    : "verify-email-page__toast--success";
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 24px;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    z-index: 10000;
    background: ${isError ? "#d32f2f" : "#2e7d32"};
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    font-family: 'Inter', sans-serif;
    opacity: 0;
    animation: fadeInOut 3s forwards;
  `;
  document.body.appendChild(toast);
};

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        showToast("Invalid verification link.", true);
        navigate("/");
        return;
      }

      // ✅ GUARD: If already verifying, do nothing
      if (isVerifyingRef.current) {
        console.log(
          "Verification already in progress — skipping duplicate call.",
        );
        return;
      }

      isVerifyingRef.current = true;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_SECRET}/api/GoTasteUser/GetEmailVerificationStatus?token=${encodeURIComponent(token)}`,
        );

        if (!response.ok) throw new Error("Network error");

        const isVerified = await response.json();

        if (isVerified === true) {
          setStatus("success");
          showToast("Email verified successfully!");
          setTimeout(() => navigate("/checkout", { replace: true }), 3000);
        } else {
          setStatus("error");
          showToast(
            "Verification failed. The link may be invalid or expired.",
            true,
          );
          setTimeout(() => navigate("/", { replace: true }), 3000);
        }
      } catch (error) {
        console.error("Verification error:", error);
        setStatus("error");
        showToast("Unable to verify email. Please try again later.", true);
        setTimeout(() => navigate("/", { replace: true }), 3000);
      } finally {
        isVerifyingRef.current = false;
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="verify-email-page">
      <div className="verify-email-page__pulse-bar"></div>
      <div className="verify-email-page__container">
        <div className="verify-email-page__icon-wrapper">
          <Loader2 className="verify-email-page__spinner-icon" />
        </div>
        <h1 className="verify-email-page__title">Verifying Your Email</h1>
        <p className="verify-email-page__subtitle">
          Please wait while we confirm your identity...
        </p>
        <div className="verify-email-page__brand-accent"></div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
